import { CustomTableSkeleton } from '@/components_v2/Table/TableSkeleton/CustomTableSkeleton';
import { Box, ButtonGroup, Flex, IconButton } from '@chakra-ui/react';
import _, { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Icons from 'react-icons/tb';
import { useTheme } from 'styled-components';

import EmptyData from '../EmptyData';
import Donuts from './components/Donut';
import { InnerTable } from './components/InnerTable';
import Search from './components/Search';
import { TabsViews, useTabsViewsContext } from './components/TabsViews';
import { ItemDataProps, TableProps } from './interfaces';

const ResponsiveTableV2: React.FC<TableProps> = ({
  columns,
  footer,
  items,
  headerHeight,
  donuts,
  searchKey,
  rowHeight,
  cancelToken,
  dashMenuOptions,
  calcInFront,
  deltaColumn1,
  deltaColumn2,
  orderItemsByProp,
  isExportable,
  onExport,
  separateDonuts,
  parentExpanded,
  level,
  setChildHeight,
  subgroupColor,
  scrollbarWidth,
  columnsFilters,
  contentInfo,
  adjustedHeight,
  width,
  height,
  tableContainerRef,
  fetchMoreOnBottomReached,
  isFetching,
  setSelectedDonuts,
  selectedDonuts,
  donuts_selection_type,
  switchColumnValueViewButton,
  tableView: tableViewProp,
  ...rest
}) => {
  const { hasTabs, globalLoading } = useTabsViewsContext();
  const theme = useTheme();

  const [tableView, setTableView] = useState<string>(tableViewProp || 'value');

  useEffect(() => {
    if (tableViewProp) {
      setTableView(tableViewProp);
    }
  }, [tableViewProp]);

  const parseItems = useCallback(
    (items: ItemDataProps[]) => {
      const parsedItems = items.map((item: any) => {
        const parsedItem = item;
        const value1 = deltaColumn1 && parsedItem[deltaColumn1].value;
        const value2 = deltaColumn2 && parsedItem[deltaColumn2].value;

        let parsedValue = 0;
        let color = '#FFEF00';

        if (value1 < value2) {
          color = '#00b400';
          parsedValue = ((value2 - value1) * 100) / (value1 === 0 ? 1 : value1);
        } else if (value1 > value2) {
          color = '#FF2E2E';
          parsedValue = ((value1 - value2) * 100) / (value2 === 0 ? 1 : value2);
        } else {
          parsedValue = 0;
        }

        parsedItem.delta = {
          color: '#000000',
          textAlign: 'center',
          value:
            value1 === '-' || value2 === '-'
              ? ''
              : `${
                  Math.floor(parsedValue) !== parsedValue
                    ? parsedValue.toFixed(2)
                    : Math.floor(parsedValue)
                }%`,
          type: 'arrow',
          backgroundColor: color,
          badgeBackgroundColor: '#ffffff',
        };

        return parsedItem;
      });

      const deltaOrderItems = (items: ItemDataProps[]) => {
        const getColorCategory = (item: ItemDataProps) => {
          if (item.delta.backgroundColor === '#00b400') return 'up';
          if (item.delta.backgroundColor === '#FF2E2E') return 'down';
          if (item.delta.backgroundColor === '#FFEF00') return 'equal';
          return 'unknown';
        };

        const sortItems = (items: ItemDataProps[]) =>
          _.orderBy(items, (item) =>
            Number(item.delta.value.toString().replace('%', '')),
          ).reverse();

        const upItems = items.filter((item) => getColorCategory(item) === 'up');
        const downItems = items.filter(
          (item) => getColorCategory(item) === 'down',
        );
        const equalItems = items.filter(
          (item) => getColorCategory(item) === 'equal',
        );

        return _.concat(sortItems(upItems), sortItems(downItems), equalItems);
      };

      return deltaOrderItems(parsedItems);
    },
    [deltaColumn1, deltaColumn2],
  );

  const hasSwitchPercentage = useMemo(() => {
    return switchColumnValueViewButton;
  }, [switchColumnValueViewButton]);

  const parsedColumns = useMemo(() => {
    const columnsViewMode = columns.map((c) => ({
      ...c,
      attribute:
        !hasSwitchPercentage ||
        switchColumnValueViewButton?.columnsWithoutChange.includes(c.key)
          ? 'value'
          : tableView,
    }));

    if (calcInFront) {
      columnsViewMode.push({
        header: '% Δ',
        key: 'delta',
        minWidth: 80,
        sortable: true,
      });
    }

    return columnsViewMode;
  }, [
    columns,
    hasSwitchPercentage,
    switchColumnValueViewButton?.columnsWithoutChange,
    tableView,
    calcInFront,
  ]);

  const [data, setData] = useState<ItemDataProps[]>(items);
  const [initialItems, setInitialItems] = useState<ItemDataProps[]>(items);
  const [searchText, setSearchText] = useState<string>('');

  function orderItems(array: ItemDataProps[], attribute: string) {
    return _.orderBy(array, (item) => {
      const { value } = item[attribute];
      const firstChar = value.toString().charAt(0);
      const isNumeric = /^\d/.test(firstChar);
      return isNumeric ? [1, value] : [0, value];
    });
  }

  useEffect(() => {
    const parsedItems = calcInFront ? parseItems(items) : items;

    const orderedItems = orderItemsByProp
      ? orderItems(parsedItems, orderItemsByProp)
      : parsedItems;

    setData(orderedItems);
    setInitialItems(orderedItems);
  }, [items, calcInFront, parseItems, orderItemsByProp]);

  return (
    <>
      {donuts && (
        <Box height={100} width={width} overflowX="auto">
          <Donuts
            data={donuts}
            separateDonuts={separateDonuts || false}
            onSelectedDonut={setSelectedDonuts}
            selectedDonuts={selectedDonuts}
            selectionType={donuts_selection_type}
          />
        </Box>
      )}
      {searchKey && (
        <Search
          initialItems={initialItems}
          searchKey={searchKey}
          setData={setData}
          setSearchText={setSearchText}
          searchText={searchText}
        />
      )}

      <Flex
        justifyContent="space-between"
        alignItems="center"
        my={hasSwitchPercentage && !level ? 6 : 0}
      >
        {hasTabs && <TabsViews />}
        {hasSwitchPercentage && !level && (
          <ButtonGroup
            alignSelf="flex-end"
            bg="gray.100"
            borderRadius="md"
            justifyContent="flex-end"
            overflow="hidden"
            p={1}
          >
            {switchColumnValueViewButton?.buttons?.map((button) => {
              const Icon = Icons[button.icon] || Icons.TbNumbers;
              return (
                <IconButton
                  aria-label="Visualizar em números absolutos"
                  icon={<Icon />}
                  onClick={() => setTableView(button.attribute)}
                  backgroundColor={
                    tableView === button.attribute
                      ? theme.colors.primary
                      : 'transparent'
                  }
                  color={tableView === button.attribute ? 'white' : 'black'}
                />
              );
            })}
          </ButtonGroup>
        )}
      </Flex>

      {globalLoading && <CustomTableSkeleton count={500} />}

      {!globalLoading && isEmpty(data) && <EmptyData>Sem Dados</EmptyData>}

      {!globalLoading && !isEmpty(data) && (
        <Box
          maxHeight={adjustedHeight}
          height="100%"
          maxWidth={width}
          marginTop={donuts || searchKey ? '10px' : 0}
          position="relative"
          overflow="auto"
        >
          <Box width="100%" height="100%" overflowX="auto">
            <InnerTable
              columns={parsedColumns}
              footer={footer}
              items={data}
              cancelToken={cancelToken}
              headerHeight={headerHeight}
              rowHeight={rowHeight || 22}
              dashMenuOptions={dashMenuOptions}
              {...rest}
              height={
                height -
                (isExportable ? 20 : 0) -
                (donuts ? 100 : 0) -
                (searchKey ? 45 : 0) -
                (headerHeight || 0)
              }
              width={width}
              onExport={onExport}
              parentExpanded={parentExpanded}
              level={level}
              setChildHeight={setChildHeight}
              subgroupColor={subgroupColor}
              scrollbarWidth={scrollbarWidth}
              columnsFilters={columnsFilters}
              contentInfo={contentInfo}
              fetchMoreOnBottomReached={fetchMoreOnBottomReached}
              isFetching={isFetching}
              tableView={tableView}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ResponsiveTableV2;
